
import React, { useState } from 'react';
import { UserProfile, Restaurant } from '../../../app/domain/types/index.ts';
import { Search, Star, Clock, MapPin, Heart, Filter, Utensils, ShoppingBag, Check } from 'lucide-react';

interface RestaurantPartnershipProps {
  user: UserProfile;
}

const RestaurantPartnership: React.FC<RestaurantPartnershipProps> = ({ user }) => {
  const [activeFilter, setActiveFilter] = useState('All');

  const restaurants: Restaurant[] = [
    { id: '1', name: 'Green Leaf Cafe', cuisine: 'Salads', rating: 4.8, deliveryTime: '20-30 min', priceRange: '$$', image: '🥗', isHealthy: true },
    { id: '2', name: 'Protein Powerhouse', cuisine: 'Bowls', rating: 4.6, deliveryTime: '30-45 min', priceRange: '$$', image: '🥩', isHealthy: true },
    { id: '3', name: 'Sushi Zen', cuisine: 'Japanese', rating: 4.9, deliveryTime: '35-50 min', priceRange: '$$$', image: '🍣', isHealthy: true },
    { id: '4', name: 'Veggie Delights', cuisine: 'Vegetarian', rating: 4.7, deliveryTime: '25-40 min', priceRange: '$$', image: '🥑', isHealthy: true },
    { id: '5', name: 'Lean Grill', cuisine: 'American', rating: 4.5, deliveryTime: '40-55 min', priceRange: '$$', image: '🍗', isHealthy: false },
  ];

  const filters = ['All', 'Healthy Choice', 'Vegetarian', 'Under 30 min', 'Top Rated'];

  const filteredRestaurants = activeFilter === 'All' ? restaurants : restaurants.filter(r => {
    if (activeFilter === 'Healthy Choice') return r.isHealthy;
    // Mock logic for other filters
    return true;
  });

  return (
    <div className="h-full flex flex-col bg-[#FAFAFA] dark:bg-[#0f0f0f] relative">

      {/* Header */}
      <div className="p-6 md:p-8 pb-4 border-b border-stone-200 dark:border-stone-800 bg-white/80 dark:bg-stone-900/80 backdrop-blur-md sticky top-0 z-20">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Healthy Dining</h1>
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <MapPin size={16} className="text-orange-500" />
            Delivering to <span className="font-medium text-gray-900 dark:text-white border-b border-dashed border-gray-300 cursor-pointer">Home</span>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search restaurants or dishes..."
              className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {filters.map(filter => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors border ${
                  activeFilter === filter 
                  ? 'bg-orange-500 text-white border-orange-500' 
                  : 'bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 text-gray-600 dark:text-gray-300 hover:bg-stone-50 dark:hover:bg-stone-700'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 md:p-8 no-scrollbar">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Featured Partners</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRestaurants.map(restaurant => (
            <div key={restaurant.id} className="bg-white dark:bg-stone-800 rounded-2xl overflow-hidden border border-stone-200 dark:border-stone-700 shadow-sm hover:shadow-xl transition-all group cursor-pointer">
              <div className="h-40 bg-stone-100 dark:bg-stone-700 flex items-center justify-center text-6xl relative">
                {restaurant.image}
                {restaurant.isHealthy && (
                  <div className="absolute top-3 left-3 bg-green-500 text-white px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1">
                    <Check size={10} /> Healthy Choice
                  </div>
                )}
                <button className="absolute top-3 right-3 p-2 bg-white/50 dark:bg-black/30 backdrop-blur-md rounded-full hover:bg-white dark:hover:bg-black transition-colors text-gray-600 dark:text-gray-300 hover:text-red-500 dark:hover:text-red-500">
                  <Heart size={16} />
                </button>
              </div>

              <div className="p-5">
                <div className="flex justify-between items-start mb-1">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-orange-500 transition-colors">{restaurant.name}</h3>
                  <span className="bg-stone-100 dark:bg-stone-700 px-2 py-0.5 rounded text-xs font-bold text-gray-600 dark:text-gray-300 flex items-center gap-1">
                    <Star size={10} className="fill-orange-400 text-orange-400" /> {restaurant.rating}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{restaurant.cuisine} • {restaurant.priceRange}</p>

                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-3 border-t border-stone-100 dark:border-stone-700">
                  <span className="flex items-center gap-1"><Clock size={14} /> {restaurant.deliveryTime}</span>
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">$0 Delivery Fee</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Promo Banner */}
        <div className="mt-10 p-8 rounded-3xl bg-gradient-to-r from-orange-500 to-red-600 text-white relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="text-3xl font-bold mb-2">Healthy Eating Rewards</h2>
            <p className="mb-6 opacity-90 max-w-md">Earn 2x community points when you order from our "Healthy Choice" partners this week.</p>
            <button className="bg-white text-orange-600 px-6 py-3 rounded-full font-bold hover:bg-stone-100 transition-colors shadow-lg">
              View Offers
            </button>
          </div>
          <div className="absolute right-0 bottom-0 opacity-20 text-[150px] -mb-10 -mr-10">
            <Utensils />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantPartnership;
